import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from './Icon.tsx';
import { panelStyle, usePopover } from './usePopover.ts';
import { useI18n } from '~/i18n/index.tsx';

/*
 * Calendar labels come from Intl, not from a hand-written table: they are locale data, not
 * product copy.
 *
 * Both tags carry `-u-ca-gregory-nu-latn` on purpose. Plain `ar-SA` resolves to the ISLAMIC
 * calendar in Chrome, which would print a Hijri month over a Gregorian ISO value, and to
 * Arabic-Indic digits, which would not match the Latin numerals every other figure in the panel
 * uses. The English tag is pinned the same way so the two locales cannot drift apart on either
 * count.
 */
const TAG = { ar: 'ar-u-ca-gregory-nu-latn', en: 'en-GB-u-ca-gregory-nu-latn' } as const;

/** The Saudi week starts on Saturday, which is JS weekday 6. */
const FIRST_WEEKDAY = 6;

const iso = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;

/** Parses `YYYY-MM-DD` as a LOCAL date — `new Date(iso)` would read it as UTC. */
function parse(value: string | undefined): Date | null {
  if (!value) {
    return null;
  }

  const [y, m, d] = value.split('-').map(Number);

  if (!y || !m || !d) {
    return null;
  }

  const date = new Date(y, m - 1, d);

  return Number.isNaN(date.getTime()) ? null : date;
}

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
const addMonths = (date: Date, count: number) => new Date(date.getFullYear(), date.getMonth() + count, 1);

const sameDay = (a: Date | null, b: Date | null) =>
  !!a &&
  !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** The six-week grid a month is drawn on, leading and trailing days included. */
function monthGrid(view: Date) {
  const first = startOfMonth(view);
  const lead = (first.getDay() - FIRST_WEEKDAY + 7) % 7;
  const start = new Date(first.getFullYear(), first.getMonth(), 1 - lead);

  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);

    return { day, outside: day.getMonth() !== view.getMonth() };
  });
}

/**
 * A date field with a calendar of our own.
 *
 * `<input type="date">` hands the picker to the OS: a Chrome-drawn panel in the system font with
 * a US-format mask, no way to style a day, and — on an Arabic-first screen — month names and a
 * week that start wherever the browser decides. The field below keeps the panel's own shell, and
 * the calendar is drawn in the panel's type, spacing and accent, with the week starting on
 * Saturday.
 *
 * The value stays ISO `YYYY-MM-DD` in and out, so nothing that consumes it changes.
 *
 * The column heads are the SHORT day name rather than the narrow one. `narrow` gives a single
 * letter — S S M T W T F in English — which is ambiguous in both scripts; every calendar a reader
 * has met prints the three-letter form instead. Arabic's short form keeps the definite article
 * (الأحد, الثلاثاء), which is the whole word and will not sit in a seven-column grid, so the
 * leading ال is trimmed to give the abbreviation Arabic calendars actually print.
 *
 * @param min ISO lower bound, @param max ISO upper bound — days outside are unpickable
 * @param variant `bare` drops the shell, for a control already inside one
 * @param clearable  show a clear button once a date is set. Opt-in, because for most fields "no
 *   date" is not a meaningful answer — but where an empty value is an instruction of its own (an
 *   open-ended date range, say) the picker has to be able to get BACK to empty, which a calendar
 *   alone cannot do.
 */
export function DatePicker({
  id,
  value,
  onChange,
  clearable = false,
  /*
   * The FORMAT, not the field's name. A caller that passes its label back in says the same thing
   * twice under a label already reading it, and still leaves the reader guessing how the date
   * should look.
   */
  placeholder = 'YYYY-MM-DD',
  min,
  max,
  disabled = false,
  invalid = false,
  variant = 'field',
  className = '',
  ...rest
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  clearable?: boolean;
  placeholder?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  invalid?: boolean;
  variant?: 'field' | 'bare';
  className?: string;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value' | 'onChange' | 'id'>) {
  const { t, locale } = useI18n();
  const { open, setOpen, close, pos, rootRef, panelRef, id: panelId } = usePopover({ align: 'start' });

  const monthLabel = useMemo(
    () => new Intl.DateTimeFormat(TAG[locale], { month: 'long', year: 'numeric' }),
    [locale],
  );

  const weekdays = useMemo(() => {
    const format = new Intl.DateTimeFormat(TAG[locale], { weekday: 'short' });

    // 2024-01-06 IS a Saturday, so it is already column 0.
    return Array.from({ length: 7 }, (_, index) =>
      format.format(new Date(2024, 0, 6 + index)).replace(/^ال/, ''),
    );
  }, [locale]);

  const selected = useMemo(() => parse(value), [value]);
  const today = useMemo(() => new Date(), []);
  const [view, setView] = useState(() => startOfMonth(selected ?? today));

  // Re-open on the month the value is in, not wherever it was left last time.
  useEffect(() => {
    if (open) {
      setView(startOfMonth(selected ?? today));
    }
  }, [open, selected, today]);

  const lower = useMemo(() => parse(min), [min]);
  const upper = useMemo(() => parse(max), [max]);
  const blocked = (day: Date) => (!!lower && day < lower) || (!!upper && day > upper);

  const days = useMemo(() => monthGrid(view), [view]);

  function take(day: Date) {
    if (blocked(day)) {
      return;
    }

    onChange(iso(day));
    close();
    rootRef.current?.querySelector('button')?.focus();
  }

  /*
   * Sibling of the control, not a child of it: the control is itself a `<button>`, and a button
   * inside a button is invalid markup that browsers silently un-nest.
   */
  const showClear = clearable && !!value && !disabled;

  return (
    <div className={`datepicker datepicker--${variant} ${className}`.trim()} ref={rootRef}>
      <button
        id={id}
        type="button"
        className={`datepicker__control${open ? ' is-open' : ''}${value ? '' : ' is-empty'}${
          invalid ? ' is-invalid' : ''
        }${showClear ? ' has-clear' : ''}`}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={() => setOpen((state) => !state)}
        aria-haspopup="dialog"
        aria-expanded={open}
        {...rest}
      >
        {variant === 'field' ? (
          <Icon name="calendar" className="datepicker__icon" size={16} strokeWidth={1.75} />
        ) : null}
        <span className="datepicker__value" dir={value ? 'ltr' : undefined}>
          {value || placeholder}
        </span>
      </button>

      {showClear ? (
        <button
          type="button"
          className="datepicker__clear"
          onClick={() => onChange('')}
          aria-label={t('common.clearDate')}
        >
          <Icon name="close" size={13} strokeWidth={2.5} />
        </button>
      ) : null}

      {open
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              role="dialog"
              aria-label={t('common.chooseDate')}
              className="datepicker__panel"
              style={panelStyle(pos)}
            >
              <div className="datepicker__head">
                {/*
                  In Arabic "previous" points right and "next" points left, so the glyphs are
                  chosen for the reading direction rather than mirrored blindly.
                */}
                <button
                  type="button"
                  className="datepicker__nav"
                  onClick={() => setView((month) => addMonths(month, -1))}
                  aria-label={monthLabel.format(addMonths(view, -1))}
                >
                  <Icon name="chevronEnd" className="ltr-flip" size={16} strokeWidth={2} />
                </button>
                <span className="datepicker__month">{monthLabel.format(view)}</span>
                <button
                  type="button"
                  className="datepicker__nav"
                  onClick={() => setView((month) => addMonths(month, 1))}
                  aria-label={monthLabel.format(addMonths(view, 1))}
                >
                  <Icon name="chevronStart" className="ltr-flip" size={16} strokeWidth={2} />
                </button>
              </div>

              <div className="datepicker__weekdays" aria-hidden="true">
                {weekdays.map((label) => (
                  <span key={label} className="datepicker__weekday">
                    {label}
                  </span>
                ))}
              </div>

              <div className="datepicker__grid">
                {days.map(({ day, outside }) => (
                  <button
                    key={iso(day)}
                    type="button"
                    className={`datepicker__day${outside ? ' is-outside' : ''}${
                      sameDay(day, selected) ? ' is-selected' : ''
                    }${sameDay(day, today) ? ' is-today' : ''}`}
                    disabled={blocked(day)}
                    aria-current={sameDay(day, today) ? 'date' : undefined}
                    aria-pressed={sameDay(day, selected)}
                    onClick={() => take(day)}
                  >
                    {day.getDate()}
                  </button>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
